import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
  Button,
} from "@mui/material";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import TextInput from "../../../../../../components/textinput/textinput";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  getTaxDetailsRequest,
  getTaxClassificationsRequest,
} from "../../../../../../redux/inspection-firms/slice";
import Loading from "../../../../../../components/loading/loading";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";
import { updateTaxDetailsRequest } from "../../../../../../utils/APICalls";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
};

interface FormValues {
  entityName?: string;
  businessName?: string;
  taxClassificationType?: string;
  // securityNumber?: string;
  taxIdNumber?: string;
}

export default function EditTaxDetails({
  showDialog,
  closeDialog,
  data,
}: props) {
  const dispatch = useDispatch();
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { taxClassificationTypes, loading, error } = useSelector(
    (state: any) => state.inspectionFirms
  );

  useEffect(() => {
    dispatch(getTaxClassificationsRequest(data?.id));
  }, [dispatch, data?.id]);

  const ValidationSchema = yup.object({
    entityName: yup.string().required("This field can't be empty"),
    // businessName: yup.string().required("This field can't be empty"),
    taxClassificationType: yup.string().required("This field can't be empty"),
    // securityNumber: yup.string().when("taxClassificationType", {
    //   is: (value: string) => value === "Individual",
    //   then: (schema) => schema.required("This field can't be empty"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    taxIdNumber: yup.string().required("This field can't be empty"),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      entityName: data?.entityName || "",
      businessName: data?.businessName || "",
      taxClassificationType: data?.taxClassificationType || "",
      // securityNumber:
      //   data?.taxClassificationType === "Individual" ? data?.taxIdNumber : "",
      taxIdNumber: data?.taxIdNumber || "",
    },
    validationSchema: ValidationSchema,
    onSubmit: async (values) => {
      const isValid = await formik.validateForm();
      if (!isValid) {
        formik.setTouched({
          entityName: true,
          businessName: true,
          taxClassificationType: true,
          // securityNumber: true,
          taxIdNumber: true,
        });
        return;
      }
      updateTaxDetails(values);
    },
  });

  const updateTaxDetails = async (values: any) => {
    const payload = {
      id: data?.id,
      entityName: values.entityName,
      businessName: values.businessName,
      taxClassificationType: values.taxClassificationType,
      taxIdNumber: values.taxIdNumber,
    };
    setShowLoading(true);
    const response = await updateTaxDetailsRequest(payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getTaxDetailsRequest({ id: data?.id }));
      hideDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const hideDialog = () => {
    formik.resetForm();
    closeDialog();
  };

  return (
    <DialogContainer
      title="Inspection Firm - Tax"
      showDialog={showDialog}
      closeDialog={closeDialog}
      onSubmit={formik.handleSubmit}
      leftButtonComponent={
        <Button
          sx={{ mx: 1 }}
          startIcon={<CloseOutlinedIcon color="error" />}
          color="error"
          onClick={() =>
            formik.resetForm({
              values: {
                entityName: "",
                businessName: "",
                taxClassificationType: "",
                taxIdNumber: "",
              },
            })
          }
          disabled={showLoading}
        >
          Clear Selection
        </Button>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Box className="user-name-layout">
          <Typography className="label-txt">
            Name of Entity/Individual
          </Typography>
          <TextInput
            className="field-input"
            id="entityName"
            name="entityName"
            variant="outlined"
            value={formik.values.entityName}
            onChange={formik.handleChange}
            error={
              formik.touched.entityName && Boolean(formik.errors.entityName)
            }
            helperText={formik.touched.entityName && formik.errors.entityName}
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">
            Business Name/DBA (if different from above)
          </Typography>
          <TextInput
            className="field-input"
            id="businessName"
            name="businessName"
            variant="outlined"
            value={formik.values.businessName}
            onChange={formik.handleChange}
          />
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Tax Classification</Typography>
          <FormControl
            component="fieldset"
            error={Boolean(
              formik.touched.taxClassificationType &&
                formik.errors.taxClassificationType
            )}
          >
            <RadioGroup
              aria-label="taxClassificationType"
              name="taxClassificationType"
              value={formik.values.taxClassificationType}
              onChange={formik.handleChange}
            >
              {taxClassificationTypes?.map((type: any) => (
                <FormControlLabel
                  key={type.taxClassificationType}
                  value={type.taxClassificationType}
                  control={<Radio />}
                  label={type.displayName}
                />
              ))}
            </RadioGroup>
            {formik.touched.taxClassificationType &&
              formik.errors.taxClassificationType && (
                <Typography color="error" sx={{ fontSize: 12, ml: 2, mt: 0.5 }}>
                  {formik.errors.taxClassificationType}
                </Typography>
              )}
          </FormControl>
        </Box>
        <Divider />
        {/* {formik.values.taxClassificationType === "Individual" ? (
          <Box className="user-name-layout">
            <Typography className="label-txt">
              Social Security Number
            </Typography>
            <TextInput
              className="field-input"
              id="securityNumber"
              name="securityNumber"
              variant="outlined"
              value={formik.values.securityNumber}
              onChange={(e) => {
                const formatted = socialSecurityNumberFormatInput(
                  e.target.value,
                  formik.values.securityNumber
                );
                formik.setFieldValue("securityNumber", formatted);
              }}
              error={
                formik.touched.securityNumber &&
                Boolean(formik.errors.securityNumber)
              }
              helperText={
                formik.touched.securityNumber && formik.errors.securityNumber
              }
            />
          </Box>
        ) : ( */}
        <Box className="user-name-layout">
          <Typography className="label-txt">Tax ID Number</Typography>
          <TextInput
            className="field-input"
            id="taxIdNumber"
            name="taxIdNumber"
            variant="outlined"
            value={formik.values.taxIdNumber}
            onChange={(e) => {
              const { value } = e.target;
              const input = value.replace(/\D/g, "");
              if (input.length <= 9) {
                formik.setFieldValue("taxIdNumber", input);
              }
            }}
            error={
              formik.touched.taxIdNumber && Boolean(formik.errors.taxIdNumber)
            }
            helperText={formik.touched.taxIdNumber && formik.errors.taxIdNumber}
          />
        </Box>
        {/* )} */}
      </form>
      {(loading || showLoading) && <Loading />}
      {(error || showErrorNotification) && (
        <ErrorNotification
          show={showErrorNotification}
          message={error || errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
    </DialogContainer>
  );
}
